/** Convierte un valor a número finito o al respaldo. */
export function toFinite(value: any, fallback: number): number {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return parsed;
  return fallback;
}

/** Limita un valor al rango indicado. */
export function clampNumber(value: any, min: number, max: number): number {
  return bounded(toFinite(value, min), min, max);
}

/** Aplica los límites inferior y superior al número. */
function bounded(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/** Calcula la penalización según el modo de captura. */
export function reviewPenaltyFor(mode: string, input: number | null, base: number): number {
  if (mode === 'raw') return penaltyFromRaw(input, base);
  return clampNumber(input, 0, 100);
}

/** Convierte el puntaje final en porcentaje de penalización. */
export function penaltyFromRaw(input: number | null, base: number): number {
  const raw = clampNumber(input, 0, base);
  if (base <= 0) return 0;
  return Math.round((1 - raw / base) * 100);
}

/** Calcula el puntaje final tras aplicar la penalización. */
export function reviewFinalScore(base: number, penalty: number): number {
  return Math.round(base * (1 - penalty / 100));
}

/** Indica si el puntaje capturado supera al calculado. */
export function isRawTooHigh(mode: string, input: number | null, base: number): boolean {
  if (mode !== 'raw') return false;
  return Number(input) > base;
}

/** Extrae el mensaje de error de una revisión fallida. */
export function signErrorText(err: any): string {
  return firstErrorText(err?.error) || 'No se pudo registrar la revisión';
}

/** Indica si el cuerpo de error está vacío. */
function hasNoErrorBody(body: any): boolean {
  return !body;
}

/** Obtiene el mensaje disponible del cuerpo de error. */
function errorBodyMessage(body: any): string | null {
  return body.message || body.error || null;
}

/** Obtiene el primer mensaje disponible del cuerpo de error. */
function firstErrorText(body: any): string | null {
  if (hasNoErrorBody(body)) return null;
  return errorBodyMessage(body);
}

/** Obtiene el id del usuario actual o vacío. */
export function currentUserId(service: { userData(): any }): string {
  const me = service.userData();
  return String((me && me.id) || '');
}

/** Indica si el periodo puede ser revisado por el auditor. */
export function canReviewPeriod(reviewMode: boolean, period: any, myId: string): boolean {
  if (!reviewMode) return false;
  return reviewable(period, myId);
}

/** Indica si el periodo está realizado y pendiente de firma. */
function reviewable(period: any, myId: string): boolean {
  if (!isRealizedPending(period)) return false;
  return auditedBy(period, myId);
}

/** Indica si el periodo realizado aún no tiene firma. */
export function isRealizedPending(period: any): boolean {
  return Boolean(period && period.realized) && !period.signedStatus;
}

/** Indica si el usuario es auditor asignado y no autor. */
function auditedBy(period: any, myId: string): boolean {
  if (!isAssignedAuditor(period, myId)) return false;
  return !isSelfCompleted(period, myId);
}

/** Indica si el usuario es el auditor asignado del periodo. */
export function isAssignedAuditor(period: any, myId: string): boolean {
  const auditor = auditorIdOf(period);
  return auditor !== '' && auditor === myId;
}

/** Obtiene el id del auditor asignado o vacío. */
export function auditorIdOf(period: any): string {
  return String((period && period.auditor && period.auditor.id) || '');
}

/** Indica si el usuario fue quien completó el periodo. */
export function isSelfCompleted(period: any, myId: string): boolean {
  if (myId === '') return false;
  return myId === realizedIdOf(period);
}

/** Obtiene el id del usuario que realizó el periodo. */
function realizedIdOf(period: any): string {
  return String((period && period.realizedUserId) || '');
}

/** Indica si la respuesta trae comentario de revisión. */
function hasReviewComment(target: any): boolean {
  return Boolean(target?.reviewComment);
}

/** Indica si el comentario del auditor es visible. */
export function canSeeReviewComment(target: any, reviewMode: boolean, signed: boolean): boolean {
  if (!hasReviewComment(target)) return false;
  return reviewMode || signed;
}

/** Texto del comentario del auditor. */
export function reviewCommentText(target: any): string {
  return String(target?.reviewComment || '');
}
